import React from 'react'
import {FormattedMessage} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'
import {imageProvider} from 'utils/imageProvider'

import {Icon} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {truncateSingleLine} from '@vio/atlas/src/utils/truncate'
import {StarRating} from '@vio/atlas/StarRating'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {hasAnchorPrice} from '@vio/core/src/offer/business/price'
import {
  applyRoundingStrategy,
  getRoundingStrategy
} from '@vio/core/src/utils/number'
import {PriceComposition} from '@vio/shared/src/search/PriceComposition'
import {ReviewRating} from '@vio/shared/src/search/ReviewRating'

import {BetterValueHotel} from './types'

const CARD_WIDTH = 156

const CardWrapper = styled.div`
  width: ${CARD_WIDTH}px;
  min-width: ${CARD_WIDTH}px;
  height: 100%;
  flex-shrink: 0;
  cursor: pointer;
`

const CardContent = styled.div`
  background-color: ${cssTheme.colors.background.neutral.primary};
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  border-radius: ${cssTheme.layout.radius.lg};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
`

const ImageWrapper = styled.div`
  position: relative;
  height: 88px;
  width: 100%;

  img {
    border-radius: 0;
  }
`

const LessBadgeWrapper = styled.div`
  position: absolute;
  top: ${cssTheme.layout.spacing.s200};
  left: ${cssTheme.layout.spacing.s200};
  z-index: 1;
`

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${cssTheme.layout.spacing.s300};
  gap: ${cssTheme.layout.spacing.s200};
`

const HotelName = styled(Text)`
  ${truncateSingleLine()}
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: auto;
`

interface Props {
  hotel: BetterValueHotel
  onClick: () => void
  /**
   * Current hotel's display-unit price (the DealScore range-bar value), the
   * comparison base for the "% less" badge.
   */
  currentHotelPrice?: number | null
  /** Render the "% less" badge ("Our advice" variant only). */
  showLessBadge?: boolean
}

export const BetterValueHotelCard = ({
  hotel,
  onClick,
  currentHotelPrice,
  showLessBadge = false
}: Props) => {
  const showTotalPrices = useSelector(getShowTotalPrices)

  const {
    hotelName,
    starRating,
    reviewScore,
    reviewCount,
    imageUrl = '',
    price,
    totalPrice,
    anchorPrice = 0,
    anchorTotalPrice = 0,
    currency
  } = hotel

  const displayPrice = showTotalPrices ? totalPrice : price
  const displayAnchorPrice = showTotalPrices ? anchorTotalPrice : anchorPrice

  // Same display unit AND same rounding on both sides (the strategy the card
  // prints with via PriceComposition) so the badge can never contradict the
  // prices the user sees.
  const roundingStrategy = getRoundingStrategy()
  const roundedDisplayPrice = applyRoundingStrategy(
    roundingStrategy,
    displayPrice
  )
  const roundedCurrentPrice =
    currentHotelPrice && currentHotelPrice > 0
      ? applyRoundingStrategy(roundingStrategy, currentHotelPrice)
      : null
  const percentLess =
    showLessBadge &&
    roundedCurrentPrice &&
    roundedCurrentPrice > 0 &&
    roundedDisplayPrice > 0
      ? Math.round(
          ((roundedCurrentPrice - roundedDisplayPrice) / roundedCurrentPrice) *
            100
        )
      : null
  const shouldShowLessBadge = percentLess !== null && percentLess >= 1

  return (
    <CardWrapper onClick={onClick} role="button" tabIndex={0}>
      <CardContent>
        <ImageWrapper>
          {shouldShowLessBadge && (
            <LessBadgeWrapper>
              <Tag
                variant="accent"
                size="sm"
                iconStart={<Icon name="CustomTrendingDown" size="xs" />}
              >
                <FormattedMessage
                  id="betterValueLessBadge"
                  defaultMessage="{percent}% less"
                  values={{percent: percentLess}}
                />
              </Tag>
            </LessBadgeWrapper>
          )}
          <Image
            imageProvider={imageProvider}
            url={imageUrl}
            isResponsive
            size="main"
          />
        </ImageWrapper>
        <DetailsWrapper>
          {starRating > 0 && <StarRating rating={starRating} size="xs" />}
          <HotelName variant="labelM" dir="auto">
            {hotelName}
          </HotelName>
          {reviewCount > 0 && (
            <ReviewRating
              rating={reviewScore}
              totalRatingCount={reviewCount}
              variant="compact"
              singleLineLabels
            />
          )}
          <PriceWrapper>
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              {showTotalPrices ? (
                <FormattedMessage
                  id="suggestionHotelCard.totalPriceLabel"
                  defaultMessage="Total from"
                />
              ) : (
                <FormattedMessage
                  id="suggestionHotelCard.priceLabel"
                  defaultMessage="Nightly from"
                />
              )}
            </Text>
            <PriceComposition
              size="lg"
              showRowLayout
              showAnchorPrice={hasAnchorPrice(displayPrice, displayAnchorPrice)}
              anchorPrice={displayAnchorPrice}
              currencyCode={currency}
              price={displayPrice}
              roundingStrategy={getRoundingStrategy()}
            />
          </PriceWrapper>
        </DetailsWrapper>
      </CardContent>
    </CardWrapper>
  )
}
